import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import AgeGate from "@/components/AgeGate";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

import Home from "@/pages/Home";
import Directory from "@/pages/Directory";
import Profile from "@/pages/Profile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Membership from "@/pages/Membership";
import Admin from "@/pages/Admin";
import Advertise from "@/pages/Advertise";
import Forum from "@/pages/Forum";
import ForumThread from "@/pages/ForumThread";
import Inbox from "@/pages/Inbox";
import LocationLanding from "@/pages/LocationLanding";
import Cities from "@/pages/Cities";
import AvailableNow from "@/pages/AvailableNow";
import Agencies from "@/pages/Agencies";
import Contact from "@/pages/Contact";
import { Requests, RequestDetail } from "@/pages/Requests";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import IOSInstallHint from "@/components/IOSInstallHint";
import Splash from "@/components/Splash";
import LiveTicker from "@/components/LiveTicker";
import ExitIntentModal from "@/components/ExitIntentModal";
import AuthCallback from "@/pages/AuthCallback";
import ChooseUsername from "@/pages/ChooseUsername";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Layout() {
  const { pathname, hash } = useLocation();
  if (hash?.includes("session_id=")) return <AuthCallback />;
  const hideFooter = ["/login", "/register", "/choose-username"].includes(pathname);
  return (
    <>
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/cities" element={<Cities />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/available-now" element={<AvailableNow />} />
          <Route path="/companion/:slug" element={<Profile />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/choose-username" element={<ChooseUsername />} />
          <Route path="/account" element={<Account />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/forum" element={<Forum />} />
          <Route path="/forum/:id" element={<ForumThread />} />
          <Route path="/agencies" element={<Agencies />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/requests" element={<Requests />} />
          <Route path="/requests/:id" element={<RequestDetail />} />
          <Route path="/locations/:city" element={<LocationLanding />} />
          <Route path="/locations/:city/:town" element={<LocationLanding />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Routes>
      </main>
      {!hideFooter && <Footer />}
      {/* Spacer so the fixed LiveTicker at the bottom doesn't cover final content */}
      {!hideFooter && <div className="h-14" aria-hidden="true" />}
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.3,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let id;
    const raf = (time) => {
      lenis.raf(time);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AgeGate />
          <Layout />
          <IOSInstallHint />
          <Splash />
          <LiveTicker />
          <ExitIntentModal />
        </BrowserRouter>
      </AuthProvider>
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#1A0810",
            border: "1px solid rgba(193,95,118,0.35)",
            color: "#fff",
            borderRadius: 0,
          },
        }}
      />
    </div>
  );
}

export default App;
